/**
 * @file: IntakeDetailsCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The single card that gathers every reservation field except the remark, one
 * per labeled row: the read-only plate, expected arrival (tap to reveal the
 * picker), the 业务类型 tag picker (the shared order-tag action sheet), the
 * mileage input, the advisor picker, and the customer-type pills.
 */

import { Typography } from '@euler/app/components/typography';
import { ReservationFormState } from '@euler/app/flows/leads/pages/create-reservation/functions/useReservationForm';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { OrderTagList } from '@euler/app/flows/order/components/OrderTagList';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import format from 'date-fns/format';
import { CalendarClock, ChevronRight } from 'lucide-react-native';
import { TextInput, View } from 'react-native';
import { Card } from './common/Card';
import { FieldRow } from './common/FieldRow';
import { SelectPill } from './common/SelectPill';

export const IntakeDetailsCard = ({ form }: { form: ReservationFormState }) => {
  const { rpx } = useResponsiveContext();
  const [datePickerVisible, showDatePicker, hideDatePicker] =
    useDateTimePicker();

  return (
    <Card>
      <FieldRow label="车牌号" first>
        <Typography.Text size={rpx(15)} color={LD.ink} bold>
          {form.licensePlateNo || '—'}
        </Typography.Text>
      </FieldRow>

      <FieldRow label="预计到店时间" required onPress={showDatePicker}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <CalendarClock size={rpx(16)} color={LD.brand} />
          <Typography.Text
            size={rpx(15)}
            color={LD.ink}
            bold
            style={{ marginLeft: rpx(6) }}
          >
            {format(form.arrival, 'yyyy-MM-dd')}
          </Typography.Text>
        </View>
      </FieldRow>
      <DateTimePicker
        isVisible={datePickerVisible}
        mode="date"
        date={form.arrival}
        onConfirm={date => {
          form.setArrival(date);
          hideDatePicker();
        }}
        onCancel={hideDatePicker}
        confirmTextIOS="确认"
        cancelTextIOS="关闭"
      />

      <FieldRow label="业务类型" required alignTop>
        <OrderTagList
          tags={form.tags}
          onAddTag={form.addTag}
          onTagRemoved={form.removeTag}
          title="选择业务类型"
          placeholder="选择业务类型"
          style={{ width: '100%', justifyContent: 'flex-end' }}
        />
      </FieldRow>

      <FieldRow label="预计里程">
        <View
          style={{ width: '100%', flexDirection: 'row', alignItems: 'center' }}
        >
          <TextInput
            value={form.mileage}
            onChangeText={t => form.setMileage(t.replace(/[^0-9]/g, ''))}
            keyboardType="number-pad"
            placeholder="—"
            placeholderTextColor={LD.mist}
            style={{
              flex: 1,
              fontSize: rpx(15),
              fontFamily: FontFamily.NotoSans.Bold,
              color: LD.ink,
              padding: 0,
              textAlign: 'right',
            }}
          />
          <Typography.Text
            size={rpx(13)}
            color={LD.mist}
            style={{ marginLeft: rpx(6) }}
          >
            公里
          </Typography.Text>
        </View>
      </FieldRow>

      <FieldRow label="服务顾问" onPress={form.onPickAgent}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Typography.Text
            size={rpx(15)}
            color={form.serviceAgent ? LD.ink : LD.mist}
            bold={form.serviceAgent ? true : undefined}
          >
            {form.serviceAgent?.name ?? '选择'}
          </Typography.Text>
          <ChevronRight
            size={rpx(16)}
            color={LD.mist}
            style={{ marginLeft: rpx(2) }}
          />
        </View>
      </FieldRow>

      <FieldRow label="客户类型" alignTop>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: rpx(8),
          }}
        >
          {form.customerTypes.map(ct => (
            <SelectPill
              key={ct.type}
              label={ct.shortName}
              active={form.customerType === ct.type}
              onPress={() => form.toggleCustomerType(ct.type)}
            />
          ))}
        </View>
      </FieldRow>
    </Card>
  );
};
